// NPM MODULES
import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Stack,
  Alert,
  Checkbox,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';

// CONFIG
import config from '../../../../Config';

// MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// COMPONENTS
import Paginator from '../../../common/Paginator';

// NETWORKING
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

// Components
import CustomModal from '../../../common/CustomModal';
import Loader from '../../../common/Loader';

const DistibutionSingle = ({
  errorHandler,
  backFunction,
  selected,
  setOpenEditModal,
  setParentModalType,
}) => {
  const [error, setError] = useState('');
  const [formFields, setFormFields] = useState({
    contactsToAdd: [],
    contactsToRemove: [],
  });
  const [saving, setSave] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentContacts, setCurrentContacts] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL ?? 10);
  const [openModal, setOpenModal] = useState(false);
  const { getDistListContacts, editDistList, addDistList, exportDistList } =
    useAddressBookRequest();

  console.log('form:', formFields);
  console.log('selected:', selected);

  /*
    Handles the change event when clicking a pagination number
  */
  const changeEvent = (value) => {
    setPageNumber(value);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  // Gets contact data to add contacts to list
  const getData = useCallback(
    async function getData() {
      if (!saving) {
        if (selected) {
          const currentData = await getDistListContacts(
            selected.distribution_list_id,
            errorHandler
          );
          console.log('current contacts:', currentData);
          setCurrentContacts(currentData);
        }
        setLoading(false);
      }
    },
    [saving]
  );

  useEffect(() => {
    getData();
  }, [getData]);

  const handleCheckboxChange = (value) => {
    setFormFields((state) => ({
      ...state,
      contactsToRemove: formFields.contactsToRemove.includes(value)
        ? state.contactsToRemove.filter((id) => id !== value)
        : [...state.contactsToRemove, value],
    }));
  };

  /*
    Handles when the Form is submitted
  */
  const handleSubmit = async () => {
    // If not already saving...
    if (!saving) {
      setSave(true);
      setError('');
      let { list_name, list_type } = formFields;

      // Needs entry_name
      if ((!list_name && !selected) || (selected && list_name === '')) {
        return setError('List Name is required');
      }

      if (!list_type && !selected) {
        return setError('List Type is required');
      }

      const combinedContacts = currentContacts.concat(formFields.contactsToAdd);
      if (combinedContacts.length === 0) {
        return setError('Distribution List must have contacts');
      }

      let response;
      if (selected) {
        response = await editDistList(
          formFields,
          selected.distribution_list_id,
          setError
        );
      } else {
        response = await addDistList(formFields, setError);
      }

      setSave(false);
      setFormFields({
        contactsToAdd: [],
        contactsToRemove: [],
      });
    }
  };

  const handleExportDistList = async () => {
    if (!selected || !selected.distribution_list_id) return;

    await exportDistList(selected.distribution_list_id, errorHandler);
  };

  /*
    Handles any errors generated
  */
  const generateError = () => {
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    // Return no error if none present
    return null;
  };

  const generateContactsTable = () => {
    let combinedContacts = currentContacts;
    if (formFields.contactsToAdd) {
      combinedContacts = currentContacts.concat(formFields.contactsToAdd);
    }

    if (saving) return <Loader />;

    if (combinedContacts.length === 0) {
      return <Typography>No contacts in distribution list.</Typography>;
    }

    const rows = combinedContacts
      .slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      )
      .map((smsContact, i) => {
        return (
          <TableRow key={`contact_${i}`}>
            <TableCell>{smsContact.contact_name || smsContact.label}</TableCell>
            <TableCell>{smsContact.contact_email}</TableCell>
            <TableCell>{smsContact.contact_tn}</TableCell>
            <TableCell>
              {smsContact.contact_type === 'global' ? 'Global' : 'Personal'}
            </TableCell>
            <TableCell align="right">
              <Checkbox
                value={smsContact.sms_contact_id}
                onChange={() => handleCheckboxChange(smsContact.sms_contact_id)}
                sx={{ padding: 0 }}
                checked={formFields.contactsToRemove.includes(
                  smsContact.sms_contact_id
                )}
              />
            </TableCell>
          </TableRow>
        );
      });

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell>{AddressBookFormattedMessages.nameText}</TableCell>
                <TableCell>{AddressBookFormattedMessages.emailText}</TableCell>
                <TableCell>{AddressBookFormattedMessages.mobileText}</TableCell>
                <TableCell>{AddressBookFormattedMessages.type}</TableCell>
                <TableCell align="right">Select</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          itemCount={combinedContacts.length}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          pageLength={pageLength}
          changeEvent={changeEvent}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Box>
    );
  };

  if (loading) return <Loader />;

  let subtitle = '';
  if (selected) {
    switch (selected.list_type) {
      case 'personal':
        subtitle = `Personal List`;
        break;
      case 'global':
        subtitle = `Global List`;
        break;
      default:
        subtitle = `Unknown Type List`;
        break;
    }
  }

  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          marginTop: 4,
          marginBottom: 1,
          justifyContent: 'space-between',
        }}
      >
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            gap: { md: 4 },
            alignItems: { md: 'end' },
          }}
        >
          <Typography variant="h1" color="secondary">
            {selected.list_name}
          </Typography>

          <Typography sx={{ color: 'grey.600' }}>
            {currentContacts.length} Contacts
          </Typography>
        </Stack>

        <Button
          variant="text"
          color="secondary"
          startIcon={<ChevronLeft />}
          onClick={backFunction}
        >
          View All Lists
        </Button>
      </Box>

      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          marginBottom: 1,
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            setOpenEditModal(true);
            setParentModalType('addEdit');
          }}
        >
          Edit List
        </Button>

        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'end',
            gap: { md: 2 },
          }}
        >
          <Button
            variant="link"
            color="secondary"
            onClick={handleExportDistList}
          >
            Export List
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            disabled={formFields.contactsToRemove.length === 0}
            onClick={() => {
              setOpenModal(true);
            }}
          >
            Delete Selected
          </Button>
        </Stack>
      </Stack>

      {generateContactsTable()}
      {generateError()}

      <CustomModal
        open={openModal}
        setOpen={setOpenModal}
        title={'Contacts'}
        width={500}
        icon="contact"
      >
        <Box>
          <Typography gutterBottom>
            {`${formFields.contactsToRemove.length}
              ${
                formFields.contactsToRemove.length === 1
                  ? 'Contact'
                  : 'Contacts '
              } Selected`}
          </Typography>

          <Typography gutterBottom>
            {AddressBookFormattedMessages.deleteListContactsBody}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            <Button
              onClick={() => {
                setOpenModal(false);
              }}
              variant="link"
              color="secondary"
            >
              {AddressBookFormattedMessages.cancelText}
            </Button>

            <Button
              onClick={handleSubmit}
              variant="contained"
              color="secondary"
              disabled={saving}
            >
              {AddressBookFormattedMessages.deleteText}
            </Button>
          </Box>
        </Box>
      </CustomModal>
    </Fragment>
  );
};

export default DistibutionSingle;
